/**
 * audio.js — the mixing desk for /clone-effect.
 *
 * The tool's audio ask is specific: per take you must be able to drop the
 * track, move it in time, or keep both — and lay a third one under them as
 * music. That is three independent things and they are built here:
 *
 *   • per-clip   volume / mute / position on the timeline / fine A-V nudge
 *   • arrangement helpers that COMPUTE a position instead of asking the user to
 *     drag until it sounds right (see arrangeTurns / findCommonOffset)
 *   • a music bed with fades, looping and auto-ducking under the speech
 *
 * Every number below comes out of one shared measurement — a 100 Hz loudness
 * envelope — so speech detection, turn-taking and ducking cannot disagree with
 * each other about where someone is talking.
 *
 * Exposes window.CloneAudio.
 */
(function () {
'use strict';
if (window.CloneAudio) return;

var SR = 48000;
var ENV_HZ = 100;              // envelope resolution: 10 ms hops

function throwIfAborted(signal) {
    if (signal && signal.aborted) throw new DOMException('Aborted', 'AbortError');
}

/* ─────────────────────────────── decode ───────────────────────────────
   Straight through the project's robust decoder chain. Rolling our own here
   would re-earn every container quirk those modules already know about. */

var decodeCache = new WeakMap();

async function decode(file, octx, signal, slug) {
    if (!file) return null;
    var cached = decodeCache.get(file);
    if (cached && cached.sampleRate === octx.sampleRate) return cached;

    var buf = null;
    if (window.AudioDecoderRobust && window.AudioDecoderRobust.robust) {
        try {
            var r = await window.AudioDecoderRobust.robust(file, octx, { slug: slug || 'clone-effect', signal: signal || null });
            if (r && r.buffer) buf = r.buffer;
        } catch (e) { if (e && e.name === 'AbortError') throw e; }
    }
    if (!buf && window.WebCodecsAudioDecode && window.WebCodecsAudioDecode.supported && window.WebCodecsAudioDecode.supported(file)) {
        try { buf = await window.WebCodecsAudioDecode.decode(file, octx); } catch (_) {}
    }
    if (!buf) {
        try { buf = await octx.decodeAudioData((await file.arrayBuffer()).slice(0)); }
        catch (e) { if (e && e.name === 'AbortError') throw e; return null; }
    }
    if (buf) decodeCache.set(file, buf);
    return buf;
}

/**
 * A context, or null — never a throw.
 *
 * The project installs a SafeAC stub that TOASTS and throws a named
 * `AudioContextUnavailable` when the browser has no Web Audio. A `if (!OAC)`
 * guard does not catch that: the constructor exists, it just refuses. Measured
 * on WebKit — the rejection escaped as an unhandled promise error and put a red
 * banner on screen before the user had asked for anything to do with sound.
 */
function offlineCtx(seconds, sr) {
    var OAC = window.OfflineAudioContext || window.webkitOfflineAudioContext;
    if (!OAC) return null;
    try {
        return new OAC(2, Math.max(1, Math.ceil(Math.max(0.02, seconds) * (sr || SR))), sr || SR);
    } catch (_) {
        return null;                       // the stub already told the user
    }
}

/**
 * Whether this browser can do the audio half of the job — asked WITHOUT
 * constructing anything.
 *
 * The SafeAC stub toasts on construction, so probing by `new OfflineAudioContext()`
 * puts a red banner on screen the moment the page opens, before the user has
 * touched a control. The stub is a bare function though, so its prototype has
 * none of the real interface: checking for `createBuffer` separates the real
 * constructor from the placeholder silently.
 */
function audioAvailable() {
    var OAC = window.OfflineAudioContext || window.webkitOfflineAudioContext;
    return !!(OAC && OAC.prototype && typeof OAC.prototype.createBuffer === 'function');
}

/* ────────────────────────────── envelope ──────────────────────────────
   RMS in 10 ms hops, mixed to mono. Everything downstream — "is anyone
   talking", "when does the line end", "how hard to duck" — is a question about
   this one curve, so it is computed once per buffer and cached. */

var envCache = new WeakMap();

function envelope(buf) {
    var hit = envCache.get(buf);
    if (hit) return hit;

    var hop = Math.max(1, Math.round(buf.sampleRate / ENV_HZ));
    var n = Math.max(1, Math.floor(buf.length / hop));
    var out = new Float32Array(n);
    var chs = [];
    for (var c = 0; c < buf.numberOfChannels; c++) chs.push(buf.getChannelData(c));

    for (var i = 0; i < n; i++) {
        var s = 0, base = i * hop, end = Math.min(buf.length, base + hop);
        for (var k = base; k < end; k++) {
            var v = 0;
            for (var ci = 0; ci < chs.length; ci++) v += chs[ci][k];
            v /= chs.length;
            s += v * v;
        }
        out[i] = Math.sqrt(s / Math.max(1, end - base));
    }
    envCache.set(buf, out);
    return out;
}

function percentile(arr, p) {
    var a = Float32Array.from(arr);
    a.sort();
    var i = Math.max(0, Math.min(a.length - 1, Math.round(p * (a.length - 1))));
    return a[i];
}

/* ────────────────────────── speech spans (VAD) ──────────────────────────
   A deliberately plain energy gate. The floor is the 20th percentile — room
   tone, which a clone take always has — and the gate sits a fixed ratio above
   it, so a quiet recording and a loud one behave the same. Hangover keeps a
   sentence from being chopped at every breath.

   @returns [{ start, end }] in seconds */
function speechSpans(buf, opts) {
    opts = opts || {};
    var env = envelope(buf);
    if (!env.length) return [];

    var floor = percentile(env, 0.2);
    var peak = percentile(env, 0.98);
    if (peak <= floor * 1.15 || peak < 1e-4) return [];    // nothing but room tone

    var thr = floor + Math.max((peak - floor) * 0.16, floor * 0.9);
    var hangover = Math.round((opts.hangoverSec || 0.28) * ENV_HZ);
    var minLen = Math.round((opts.minSpanSec || 0.20) * ENV_HZ);

    var spans = [];
    var inSpan = false, spanStart = 0, quiet = 0;
    for (var i = 0; i < env.length; i++) {
        if (env[i] >= thr) {
            if (!inSpan) { inSpan = true; spanStart = i; }
            quiet = 0;
        } else if (inSpan) {
            quiet++;
            if (quiet > hangover) {
                var end = i - quiet;
                if (end - spanStart >= minLen) spans.push({ start: spanStart / ENV_HZ, end: end / ENV_HZ });
                inSpan = false;
            }
        }
    }
    if (inSpan && env.length - spanStart >= minLen) {
        spans.push({ start: spanStart / ENV_HZ, end: env.length / ENV_HZ });
    }
    return spans;
}

/* ─────────────────────── arrangement: take turns ───────────────────────
   The core timing problem of a clone dialogue: two takes recorded one after
   the other, each with the performer waiting through the OTHER half of the
   conversation in silence. Dropped on a timeline at t=0 they talk over each
   other.

   Correlation cannot fix this — the takes share no signal, they are different
   words. What DOES fix it is reading where each take actually speaks and
   placing the clips so the lines alternate.

   @returns [{ clipIndex, start, trimIn, trimOut }] — proposed placements */
function arrangeTurns(entries, gapSec) {
    var gap = gapSec == null ? 0.35 : gapSec;
    var placed = [];
    var cursor = 0;

    for (var i = 0; i < entries.length; i++) {
        var e = entries[i];
        var spans = e.spans || [];
        var dur = e.duration || 0;

        if (!spans.length) {
            // No detectable speech (music-only take, or the mic was off): it
            // still deserves a place, just not a turn — it lands where it is.
            placed.push({ clipIndex: e.clipIndex, start: cursor, speech: null });
            cursor += dur + gap;
            continue;
        }
        // Spans are measured on the WHOLE file, while the clip may start later
        // than the file does. Without this subtraction a trimmed take is placed
        // as if its dead air were still there, and every line lands late by
        // exactly the amount that was cut.
        var tIn = e.trimIn || 0;
        var tOut = e.trimOut > tIn ? e.trimOut : (tIn + dur);
        var first = Math.max(0, Math.min(spans[0].start, tOut) - tIn);
        var last = Math.max(first, Math.min(spans[spans.length - 1].end, tOut) - tIn);

        // Line up the FIRST word with the cursor, keeping the clip's own
        // pre-roll intact — that pre-roll is the performer's silent reaction to
        // the other line, and cutting it is what makes clone edits feel robotic.
        var start = cursor - first;
        placed.push({ clipIndex: e.clipIndex, start: start, speech: { first: first, last: last } });
        cursor = start + last + gap;
    }

    // Never leave the composition starting before zero.
    var minStart = 0;
    for (var j = 0; j < placed.length; j++) minStart = Math.min(minStart, placed[j].start);
    if (minStart < 0) for (var k = 0; k < placed.length; k++) placed[k].start -= minStart;
    return placed;
}

/* ────────────────────── trimming the dead air ──────────────────────────
   Takes for a clone dialogue are bookended by dead air: the performer walks
   into frame, settles, waits for the mental cue, speaks, then reaches for the
   camera. Measured on a two-take fixture, that dead air was HALF the finished
   composition — 15.1 s of which 7.8 s was nobody speaking.

   ⚠️ Cutting it naively is worse than leaving it. Both takes carry the room's
   own noise floor; trimming to the speech removes the tone as well, and where
   the tone stops the result drops to digital silence. Measured: a 0.35 s turn
   gap survived it (handles reached across), 0.8 s produced four holes and
   1.5 s produced twenty-two, each 67 dB below the floor. A gap of silence is
   far more audible than the +2.2 dB the two takes' tone adds when layered.

   So the handle kept around each line is DERIVED from the gap rather than
   picked: half the gap on each side of a boundary, plus a margin. Then the
   takes always overlap across the join and the tone never stops. Where the
   source has no dead air to give, the gap is capped to what the material can
   cover — a shorter pause is a choice, a hole is a defect.

   @returns { trims:[{clipIndex,trimIn,trimOut,cut}], gap, removed, capped } */
var TRIM_END_HEAD = 0.30;   // composition start: enough not to cut a consonant
var TRIM_END_TAIL = 0.40;   // composition end: room to breathe out
var TRIM_MIN_HANDLE = 0.15;

function planTrims(entries, gapSec) {
    var gap = gapSec == null ? 0.35 : gapSec;
    var want = Math.max(TRIM_MIN_HANDLE, gap / 2 + 0.10);
    var n = entries.length;
    var info = [], i;

    for (i = 0; i < n; i++) {
        var e = entries[i];
        var spans = e.spans || [];
        var dur = e.srcDuration || e.duration || 0;
        if (!spans.length || !(dur > 0.05)) { info.push(null); continue; }

        var first = spans[0].start;
        var last = spans[spans.length - 1].end;
        if (!(last > first)) { info.push(null); continue; }

        // The ends of the composition face nothing, so they need no coverage —
        // only enough not to clip the first and last syllable.
        var headWant = (i === 0) ? TRIM_END_HEAD : want;
        var tailWant = (i === n - 1) ? TRIM_END_TAIL : want;
        var head = Math.min(headWant, first);
        var tail = Math.min(tailWant, Math.max(0, dur - last));

        info.push({
            clipIndex: e.clipIndex,
            trimIn: Math.max(0, first - head),
            trimOut: Math.min(dur, last + tail),
            head: head, tail: tail, dur: dur
        });
    }

    // A pause can only be as long as the room tone that has to cover it.
    var maxGap = Infinity;
    for (i = 0; i + 1 < n; i++) {
        if (!info[i] || !info[i + 1]) continue;      // an untrimmed take keeps its own tone
        maxGap = Math.min(maxGap, info[i].tail + info[i + 1].head - 0.02);
    }
    var gapOut = gap, capped = false;
    if (isFinite(maxGap) && gap > maxGap) { gapOut = Math.max(0, maxGap); capped = true; }

    var trims = [], removed = 0;
    for (i = 0; i < n; i++) {
        if (!info[i]) continue;
        var cut = info[i].dur - (info[i].trimOut - info[i].trimIn);
        if (cut <= 0.05) continue;                   // already tight — say nothing
        removed += cut;
        trims.push({ clipIndex: info[i].clipIndex, trimIn: info[i].trimIn, trimOut: info[i].trimOut, cut: cut });
    }
    return { trims: trims, gap: gapOut, removed: removed, capped: capped };
}

/* ──────────────────── arrangement: shared cue (clap) ────────────────────
   The other half of the sync problem, for the case where the takes DO share a
   signal: one clap, one phone beep, the same music playing in the room. Then
   cross-correlating the envelopes finds the true offset.

   Reported with a confidence, because on two takes that share nothing this
   returns a peak all the same — a number without a confidence would silently
   move a clip by four seconds for no reason.

   @returns { offset, confidence } — seconds to ADD to b's start */
function findCommonOffset(bufA, bufB, maxLagSec) {
    var a = envelope(bufA), b = envelope(bufB);
    if (a.length < 20 || b.length < 20) return { offset: 0, confidence: 0 };

    var maxLag = Math.round((maxLagSec == null ? 12 : maxLagSec) * ENV_HZ);
    var na = norm(a), nb = norm(b);

    var best = 0, bestScore = -Infinity;
    var scores = [];
    for (var lag = -maxLag; lag <= maxLag; lag++) {
        var s = 0, n = 0;
        var i0 = Math.max(0, -lag), i1 = Math.min(na.length, nb.length - lag);
        if (i1 - i0 < 30) { scores.push(-Infinity); continue; }
        for (var i = i0; i < i1; i++) { s += na[i] * nb[i + lag]; n++; }
        var sc = s / n;
        scores.push(sc);
        if (sc > bestScore) { bestScore = sc; best = lag; }
    }

    // Confidence = how far the peak stands above the typical score. A genuine
    // shared cue spikes; unrelated takes give a flat field.
    var mean = 0, cnt = 0;
    for (var q = 0; q < scores.length; q++) if (isFinite(scores[q])) { mean += scores[q]; cnt++; }
    mean = cnt ? mean / cnt : 0;
    var sd = 0;
    for (var r = 0; r < scores.length; r++) if (isFinite(scores[r])) sd += (scores[r] - mean) * (scores[r] - mean);
    sd = cnt ? Math.sqrt(sd / cnt) : 0;
    var z = sd > 1e-9 ? (bestScore - mean) / sd : 0;

    return {
        offset: -best / ENV_HZ,
        confidence: Math.max(0, Math.min(1, (z - 2.5) / 4))
    };
}

function norm(x) {
    var m = 0;
    for (var i = 0; i < x.length; i++) m += x[i];
    m /= x.length;
    var sd = 0;
    for (var j = 0; j < x.length; j++) sd += (x[j] - m) * (x[j] - m);
    sd = Math.sqrt(sd / x.length) || 1;
    var out = new Float32Array(x.length);
    for (var k = 0; k < x.length; k++) out[k] = (x[k] - m) / sd;
    return out;
}

/* ───────────────────────────── the mix ─────────────────────────────
   Speech and music are rendered SEPARATELY and summed by hand, because the
   ducking gain has to be derived from the finished speech bus. Doing it with a
   live GainNode automation would mean guessing the curve before the speech
   exists. Gains are summed without normalisation, so muting one take does not
   make the other jump in level. */

async function buildMix(project, signal, onProgress) {
    var CS = window.CloneScene;
    var total = CS.projectDuration(project);
    if (!(total > 0)) return null;

    var wantSpeech = project.clips.some(function (c) {
        return c && c.visible && c.hasAudio && !c.muted && c.volume > 0;
    });
    var wantMusic = !!(project.music && project.music.file && project.music.volume > 0);
    if (!wantSpeech && !wantMusic) return null;

    var speech = null;
    if (wantSpeech) {
        speech = await renderSpeech(project, total, signal);
        onProgress && onProgress(0.5);
    }
    var music = null;
    if (wantMusic) {
        music = await renderMusic(project, total, signal);
        onProgress && onProgress(0.85);
    }

    if (speech && !music) return speech;
    if (music && !speech) return music;
    if (!speech && !music) return null;

    return sumWithDucking(speech, music, project.music);
}

async function renderSpeech(project, total, signal) {
    var CS = window.CloneScene;
    var octx = offlineCtx(total, SR);
    if (!octx) return null;

    for (var i = 0; i < project.clips.length; i++) {
        throwIfAborted(signal);
        var c = project.clips[i];
        if (!c || !c.visible || !c.hasAudio || c.muted || !(c.volume > 0)) continue;

        var buf = await decode(c.file, octx, signal);
        if (!buf || !buf.length) { c.hasAudio = false; continue; }

        var dur = Math.min(CS.clipDuration(c), Math.max(0, buf.duration - c.trimIn));
        if (dur <= 0.01) continue;

        // The clip's own position plus its fine A/V nudge. A negative result
        // means the audio would have to start before the composition does, so
        // the head is trimmed instead of shifting everything else.
        var when = c.start + (c.audioOffset || 0);
        var srcOff = c.trimIn;
        if (when < 0) { srcOff += -when; dur -= -when; when = 0; }
        if (dur <= 0.01) continue;
        if (when >= total) continue;
        dur = Math.min(dur, total - when);
        if (srcOff >= buf.duration) continue;

        var g = octx.createGain();
        g.gain.value = c.volume;
        g.connect(octx.destination);
        var src = octx.createBufferSource();
        src.buffer = buf;
        src.connect(g);
        src.start(when, srcOff, dur);
    }
    return await octx.startRendering();
}

async function renderMusic(project, total, signal) {
    var m = project.music;
    var CS = window.CloneScene;
    var octx = offlineCtx(total, SR);
    if (!octx) return null;

    var buf = await decode(m.file, octx, signal);
    if (!buf || !buf.length) return null;

    var pieceDur = Math.min(CS.musicDuration(m) || buf.duration, Math.max(0, buf.duration - m.trimIn));
    if (pieceDur <= 0.02) return null;

    var master = octx.createGain();
    master.connect(octx.destination);

    var start = Math.max(0, m.start || 0);
    var span = Math.max(0, total - start);
    if (span <= 0.02) return null;

    if (m.loop && pieceDur < span - 0.05) {
        var t = start;
        while (t < total - 0.01) {
            var seg = Math.min(pieceDur, total - t);
            if (seg <= 0.01) break;
            var s = octx.createBufferSource();
            s.buffer = buf; s.connect(master);
            s.start(t, m.trimIn, seg);
            t += pieceDur;
        }
    } else {
        var s2 = octx.createBufferSource();
        s2.buffer = buf; s2.connect(master);
        s2.start(start, m.trimIn, Math.min(pieceDur, span));
    }

    // Fades on the master, so a looped bed fades as one bed and not per lap.
    var vol = m.volume;
    var fi = Math.max(0, Math.min(m.fadeIn || 0, span / 2));
    var fo = Math.max(0, Math.min(m.fadeOut || 0, span / 2));
    var gp = master.gain;
    gp.setValueAtTime(fi > 0.01 ? 0.0001 : vol, start);
    if (fi > 0.01) gp.exponentialRampToValueAtTime(Math.max(0.0001, vol), start + fi);
    var endAt = start + span;
    if (fo > 0.01) {
        gp.setValueAtTime(vol, Math.max(start + fi, endAt - fo));
        gp.exponentialRampToValueAtTime(0.0001, endAt);
    }
    return await octx.startRendering();
}

/**
 * Sum speech + music, pulling the music down while anyone is talking.
 *
 * Attack is fast (60 ms) and release slow (450 ms): the bed has to be out of
 * the way before the first syllable and must not pump back up between words.
 * The gain curve is computed on the speech envelope at 100 Hz then linearly
 * interpolated per sample, which is inaudible at these time constants and
 * avoids a per-sample envelope follower.
 */
function makeBuffer(channels, length, sr) {
    if (typeof AudioBuffer === 'function') {
        try { return new AudioBuffer({ numberOfChannels: channels, length: length, sampleRate: sr }); }
        catch (_) {}
    }
    // Safari <15 has no AudioBuffer constructor — an OfflineAudioContext of the
    // right size will hand one over without rendering anything.
    var octx = offlineCtx(length / sr, sr);
    return octx ? octx.createBuffer(channels, length, sr) : null;
}

function sumWithDucking(speech, music, cfg) {
    var n = Math.max(speech.length, music.length);
    var out = makeBuffer(2, n, SR);
    if (!out) return speech;               // no buffer to mix into — speech alone

    var duck = !!(cfg && cfg.duck);
    var depth = Math.max(0, Math.min(0.95, cfg && cfg.duckDepth != null ? cfg.duckDepth : 0.75));

    var gainCurve = null;
    if (duck) {
        var env = envelope(speech);
        var floor = percentile(env, 0.25);
        var peak = percentile(env, 0.97);
        var thr = floor + Math.max((peak - floor) * 0.12, 1e-5);

        var atk = Math.exp(-1 / (0.060 * ENV_HZ));
        var rel = Math.exp(-1 / (0.450 * ENV_HZ));
        gainCurve = new Float32Array(env.length);
        var g = 1;
        for (var i = 0; i < env.length; i++) {
            var target = env[i] > thr ? (1 - depth) : 1;
            var coef = target < g ? atk : rel;
            g = target + (g - target) * coef;
            gainCurve[i] = g;
        }
    }

    var hop = SR / ENV_HZ;
    for (var ch = 0; ch < 2; ch++) {
        var sBuf = speech.numberOfChannels > ch ? speech.getChannelData(ch) : speech.getChannelData(0);
        var mBuf = music.numberOfChannels > ch ? music.getChannelData(ch) : music.getChannelData(0);
        var o = out.getChannelData(ch);
        for (var k = 0; k < n; k++) {
            var sv = k < sBuf.length ? sBuf[k] : 0;
            var mv = k < mBuf.length ? mBuf[k] : 0;
            if (gainCurve) {
                var pos = k / hop;
                var i0 = Math.min(gainCurve.length - 1, Math.floor(pos));
                var i1 = Math.min(gainCurve.length - 1, i0 + 1);
                var f = pos - i0;
                mv *= gainCurve[i0] * (1 - f) + gainCurve[i1] * f;
            }
            var v = sv + mv;
            o[k] = v > 1 ? 1 : (v < -1 ? -1 : v);
        }
    }
    return out;
}

/* WAV bytes — needed by the split export lane (Firefox has no AAC encoder, so
   the premixed PCM is handed to one cheap FFmpeg audio-only pass). */
function toWavBytes(buf) {
    var nch = Math.min(2, buf.numberOfChannels), len = buf.length, sr = buf.sampleRate;
    var ab = new ArrayBuffer(44 + len * nch * 2), dv = new DataView(ab);
    function ws(o, s) { for (var i = 0; i < s.length; i++) dv.setUint8(o + i, s.charCodeAt(i)); }
    ws(0, 'RIFF'); dv.setUint32(4, 36 + len * nch * 2, true); ws(8, 'WAVE'); ws(12, 'fmt ');
    dv.setUint32(16, 16, true); dv.setUint16(20, 1, true); dv.setUint16(22, nch, true);
    dv.setUint32(24, sr, true); dv.setUint32(28, sr * nch * 2, true); dv.setUint16(32, nch * 2, true);
    dv.setUint16(34, 16, true); ws(36, 'data'); dv.setUint32(40, len * nch * 2, true);
    var o = 44, chs = [];
    for (var c = 0; c < nch; c++) chs.push(buf.getChannelData(c));
    for (var i2 = 0; i2 < len; i2++) {
        for (var c2 = 0; c2 < nch; c2++) {
            var v = Math.max(-1, Math.min(1, chs[c2][i2]));
            dv.setInt16(o, v < 0 ? v * 0x8000 : v * 0x7FFF, true); o += 2;
        }
    }
    return new Uint8Array(ab);
}

window.CloneAudio = {
    SR: SR,
    ENV_HZ: ENV_HZ,
    decode: decode,
    offlineCtx: offlineCtx,
    audioAvailable: audioAvailable,
    envelope: envelope,
    speechSpans: speechSpans,
    arrangeTurns: arrangeTurns,
    planTrims: planTrims,
    findCommonOffset: findCommonOffset,
    buildMix: buildMix,
    toWavBytes: toWavBytes
};
})();
